<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";
import { maskCurrencyToFloat } from "@/common/utils/mask";

import { adminPlanGet, adminPlanPatch } from "@/common/api/rest/plan";

import type { TranslationFieldsType } from "@/common/types/translation";
import type { PlanType, PlanPayloadType } from "@/common/types/plan";

import { PanelTheme } from "@/common/themes";
import { FormPlan, ListTranslate } from "@/components";
import { breadcrumbSimple, svgIcon } from "@/common/components";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const positionData = ref<PlanType | null>(null);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const planId = computed(() => route.params.plan as string);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "plans",
    label: t("common.labels.plans"),
    url: "/register/plan",
  },
  {
    key: "edit",
    label: t("common.labels.edit"),
  },
]);

function fetchData() {
  adminPlanGet(planId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        positionData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<PlanPayloadType>) {
  let newPayload = { ...payload };

  if (payload.price) {
    newPayload.price = maskCurrencyToFloat(
      payload.price,
      (payload.currency ?? positionData.value?.currency) as any
    );
  }

  adminPlanPatch(planId.value, newPayload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function back() {
  router.push(`/register/plan`);
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="plans" size="2rem" />
          {{ t("plan.labels.plan_edit") }}
        </div>

        <div class="actions">
          <button class="btn btn-default" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <form-plan
          :data="positionData"
          :is-patch="true"
          @update:data="patchData($event)"
        />

        <list-translate
          :owner-id="planId"
          :language="locale.toLowerCase()"
          :fields="translationFields"
        />
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;

      &-actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 1rem;
      }

      &-title {
        font-size: 1.25rem;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--text-medium);
        margin-block-end: 0.75rem;
      }

      &-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(200px, 1fr));
        gap: 1rem;

        &-item {
          flex: 1 1 200px;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          border: 2px solid var(--border);
          border-radius: 0.5rem;
          padding-block: 1rem;
          padding-inline: 1.25rem;
          cursor: pointer;

          &:hover {
            border-color: var(--primary);
          }

          &-title {
            font-size: 1.25rem;
            font-weight: 600;
            text-transform: uppercase;
          }

          &-description {
            font-size: 1rem;
            color: var(--text-light);
          }
        }
      }
    }
  }
}
</style>
